import React from "react";
import { Box, Slider, Typography } from "@mui/material";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";
import { getUiLocale } from "../../../../shared/utils/relativeTime";

interface PercentageTuneSliderProps {
  /** Percentage value (0-100) */
  value: number;
  onChange: (percentage: number) => void;
  /** Base cost that the percentage is applied on top of */
  baseValue: number;
  /** Unit label, e.g. "EUR" */
  unit?: string;
  /** Optional label override */
  label?: string;
}

const marks = [
  { value: 0, label: "0%" },
  { value: 25, label: "25%" },
  { value: 50, label: "50%" },
  { value: 75, label: "75%" },
  { value: 100, label: "100%" },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat(getUiLocale(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatValue = (value: number, unit: string) => {
  if (unit === "EUR") return formatCurrency(value);
  return `${value.toFixed(1)} ${unit}`;
};

const PercentageTuneSlider = ({
  value,
  onChange,
  baseValue,
  unit = "EUR",
  label,
}: PercentageTuneSliderProps) => {
  const { translate } = useTranslation(["serviceOffers"]);

  const adjustedValue = baseValue * (1 + value / 100);
  const displayLabel =
    label || translate("percentageSlider.fineTune", { percent: value });

  return (
    <Box sx={{ px: 1, pt: 0.5 }}>
      {/* Label + value preview */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: dt.font,
            fontSize: "11px",
            color: dt.textSecondary,
          }}
        >
          {displayLabel}
        </Typography>

        {/* Base -> Adjusted preview */}
        {baseValue > 0 && value > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                color: dt.textMuted,
                textDecoration: "line-through",
              }}
            >
              {formatValue(baseValue, unit)}
            </Typography>
            <Typography
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                color: dt.textMuted,
              }}
            >
              {"\u2192"}
            </Typography>
            <Typography
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                fontWeight: 600,
                color: dt.blue,
              }}
            >
              {formatValue(adjustedValue, unit)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Slider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Slider
          value={value}
          min={0}
          max={100}
          step={5}
          marks={marks}
          onChange={(_, newValue) => onChange(newValue as number)}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `+${v}%`}
          sx={{
            color: dt.blue,
            height: 4,
            "& .MuiSlider-thumb": {
              width: 16,
              height: 16,
              backgroundColor: "#fff",
              border: `2px solid ${dt.blue}`,
              boxShadow: `0 0 0 4px ${dt.blueGlow}`,
              "&:hover, &.Mui-focusVisible": {
                boxShadow: `0 0 0 6px ${dt.blueGlow}`,
              },
            },
            "& .MuiSlider-track": {
              borderRadius: 2,
            },
            "& .MuiSlider-rail": {
              backgroundColor: "#E5E7EB",
              opacity: 1,
            },
            "& .MuiSlider-markLabel": {
              fontFamily: dt.fontMono,
              fontSize: "10px",
              color: dt.textMuted,
              top: 28,
            },
            "& .MuiSlider-mark": {
              backgroundColor: "#D1D5DB",
              width: 2,
              height: 8,
              borderRadius: 1,
            },
            "& .MuiSlider-valueLabel": {
              fontFamily: dt.fontMono,
              fontSize: "11px",
              fontWeight: 600,
              backgroundColor: dt.blue,
              borderRadius: "6px",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PercentageTuneSlider;
